import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import Layout from "@/components/Layout";
import { useTeam } from "@/context/TeamContext";
import { downloadEventClip, downloadPlayerReel, jobStatusLabel } from "@/lib/clipDownload";
import { toast } from "sonner";
import { SoccerBall, ShieldCheck, ChartBar, CalendarBlank, ArrowsInLineVertical, CaretDown, CaretUp, DownloadSimple, SpinnerGap, X, Square, Play, Stop } from "@phosphor-icons/react";

// Reused motion presets — module-scoped so framer-motion isn't handed a
// brand new object literal (and therefore treated as a changed prop) on
// every render of this page.
const EXPAND_COLLAPSE_ANIMATION = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
};
const STAGGER_ROW_INITIAL = { opacity: 0, y: 6 };
const STAGGER_ROW_ANIMATE = { opacity: 1, y: 0 };


function formatGameDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function formatEventTime(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
const TYPE_LABEL = { goal: "Goal", shot: "Shot", save: "Save", yellow_card: "Yellow Card", red_card: "Red Card", highlight: "Highlight" };
const TYPE_CLS = {
  goal: "bg-[#CCFF00]/15 text-[#CCFF00]",
  shot: "bg-blue-500/15 text-blue-400",
  save: "bg-teal-500/15 text-teal-400",
  yellow_card: "bg-yellow-500/15 text-yellow-400",
  red_card: "bg-red-500/15 text-red-400",
  highlight: "bg-fuchsia-500/15 text-fuchsia-400",
};
const DRILLDOWN_TITLES = {
  goals_for: "Goals scored",
  goals_against: "Goals conceded",
  clean_sheets: "Clean sheet games",
  yellow_cards: "Yellow cards",
  red_cards: "Red cards",
};
const GRID_COLS = "grid-cols-[32px_minmax(120px,1fr)_44px_36px_36px_36px_36px_36px_36px_44px_16px]";
const TABLE_MIN_WIDTH = "min-w-[600px]";
const MAX_REEL_CLIPS = 20;
const REEL_PRE_ROLL = 10;
const REEL_POST_ROLL = 5;
function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function Stats() {
  const navigate = useNavigate();
  const { currentTeam, currentTeamId, currentRole, teams, loading: teamsLoading } = useTeam();
  const isViewer = currentRole === "viewer";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [teamDrilldown, setTeamDrilldown] = useState(null); // "goals_for" | "goals_against" | "clean_sheets" | null
  const [downloadingId, setDownloadingId] = useState(null);
  const [clipDownloadStage, setClipDownloadStage] = useState(null);
  const [sort, setSort] = useState({ key: "jersey_number", dir: "asc" });

  // --- Cross-game player highlights reel: coach ticks any combination of
  // this player's logged events (goals/assists/shots/saves/highlights,
  // possibly spanning multiple games) and can Play them back to back or
  // Download a stitched reel. Unlike the single-game reel on Game Detail,
  // consecutive clips here may come from a DIFFERENT game's video (YouTube
  // or uploaded), so playback swaps the active player element as needed. ---
  const [selectedEventIds, setSelectedEventIds] = useState(new Set());
  const [reelActive, setReelActive] = useState(false);
  const [reelIndexDisplay, setReelIndexDisplay] = useState(0);
  const [reelClips, setReelClips] = useState([]);
  const [reelDownloading, setReelDownloading] = useState(false);
  const [reelDownloadStage, setReelDownloadStage] = useState(null);
  const [reelStartToken, setReelStartToken] = useState(0);
  const reelYtContainerRef = useRef(null);
  const reelYtPlayerRef = useRef(null);
  const reelVideoRef = useRef(null);
  const reelBunnyIframeRef = useRef(null);
  const reelBunnyPlayerRef = useRef(null);
  const reelBunnyVideoKeyRef = useRef(null);   // `${libraryId}/${videoId}` currently loaded in the iframe
  const reelBunnyCurrentTimeRef = useRef(0);   // kept fresh via player.js 'timeupdate' (its getCurrentTime is async/callback-based)
  const reelIntervalRef = useRef(null);
  const reelIndexRef = useRef(0);
  const reelClipsRef = useRef([]);
  const lastReelVideoSrcRef = useRef(null);

  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { key, dir: key === "name" || key === "position" ? "asc" : "desc" };
    });
  };

  const load = useCallback(async () => {
    if (!currentTeamId) return;
    try {
      const res = await api.get(`/teams/${currentTeamId}/season-stats`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [currentTeamId]);

  useEffect(() => {
    if (teamsLoading) return;
    if (teams.length === 0) {
      navigate("/setup");
      return;
    }
    if (currentTeamId) {
      setLoading(true);
      setExpandedPlayerId(null);
      setTeamDrilldown(null);
      setSelectedEventIds(new Set());
      stopReel();
      load();
    }
  }, [teamsLoading, teams.length, currentTeamId, load, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loads the YouTube IFrame API script once (needed so the cross-game
  // reel can seek/play a YouTube-linked game's video without a full page
  // navigation — same script GameDetail.jsx loads, safe to load twice).
  useEffect(() => {
    if (window.YT?.Player) return;
    if (document.getElementById("yt-iframe-api")) return;
    const script = document.createElement("script");
    script.id = "yt-iframe-api";
    script.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    return () => {
      if (reelIntervalRef.current) clearInterval(reelIntervalRef.current);
      if (reelYtPlayerRef.current) {
        try { reelYtPlayerRef.current.destroy(); } catch { /* already gone */ }
      }
    };
  }, []);

  const players = useMemo(() => data?.players || [], [data]);
  const events = data?.events || [];
  const cleanSheetGames = data?.clean_sheet_games || [];
  const totals = data?.totals || { games_played: 0, goals_for: 0, goals_against: 0, goal_diff: 0, clean_sheets: 0, yellow_cards: 0, red_cards: 0 };
  const withStats = players.filter((p) => p.goals > 0 || p.assists > 0 || p.shots > 0 || p.saves > 0 || p.clean_sheets > 0 || p.yellow_cards > 0 || p.red_cards > 0);

  const sortedPlayers = useMemo(() => {
    const arr = [...players];
    arr.sort((a, b) => {
      let av, bv;
      if (sort.key === "name") {
        av = `${a.first_name} ${a.last_name}`.toLowerCase();
        bv = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (sort.key === "position") {
        av = (a.position || "").toLowerCase();
        bv = (b.position || "").toLowerCase();
      } else {
        av = a[sort.key] ?? 0;
        bv = b[sort.key] ?? 0;
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return (a.jersey_number ?? 0) - (b.jersey_number ?? 0);
    });
    return arr;
  }, [players, sort]);

  const handleDownload = async (ev) => {
    setDownloadingId(ev.id);
    setClipDownloadStage("Preparing your download…");
    try {
      await downloadEventClip(currentTeamId, ev.game_id, ev.id, (job) => setClipDownloadStage(jobStatusLabel(job)));
      toast.success("Clip downloaded");
    } catch (err) {
      toast.error(err.message || "Could not download clip");
    } finally {
      setDownloadingId(null);
      setClipDownloadStage(null);
    }
  };

  const toggleEventSelected = (id) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_REEL_CLIPS) {
          toast.error(`You can select up to ${MAX_REEL_CLIPS} clips at a time.`);
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  };

  const ensureReelYtPlayer = (videoId, startSeconds) =>
    new Promise((resolve) => {
      const create = () => {
        if (!reelYtContainerRef.current) { resolve(null); return; }
        if (reelYtPlayerRef.current?.loadVideoById) {
          reelYtPlayerRef.current.loadVideoById(videoId, startSeconds);
          resolve(reelYtPlayerRef.current);
          return;
        }
        const player = new window.YT.Player(reelYtContainerRef.current, {
          videoId,
          playerVars: { rel: 0, modestbranding: 1, start: Math.floor(startSeconds) },
          events: { onReady: (e) => resolve(e.target) },
        });
        reelYtPlayerRef.current = player;
      };
      if (window.YT?.Player) create();
      else {
        const prevReady = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => { prevReady?.(); create(); };
      }
    });

  // Bunny Stream's official iframe-control library (player.js) — loaded
  // once, lazily, the same way the YouTube IFrame API is loaded above.
  useEffect(() => {
    if (window.playerjs || document.getElementById("bunny-playerjs")) return;
    const script = document.createElement("script");
    script.id = "bunny-playerjs";
    script.src = "https://assets.mediadelivery.net/playerjs/playerjs-latest.min.js";
    document.body.appendChild(script);
  }, []);

  const ensureReelBunnyPlayer = (libraryId, videoId, startSeconds) =>
    new Promise((resolve) => {
      const iframe = reelBunnyIframeRef.current;
      if (!iframe) { resolve(null); return; }
      const key = `${libraryId}/${videoId}`;
      if (reelBunnyVideoKeyRef.current === key && reelBunnyPlayerRef.current) {
        resolve(reelBunnyPlayerRef.current);
        return;
      }
      reelBunnyVideoKeyRef.current = key;
      reelBunnyPlayerRef.current = null;
      const attach = () => {
        const player = new window.playerjs.Player(iframe);
        player.on("ready", () => { reelBunnyPlayerRef.current = player; resolve(player); });
        player.on("timeupdate", (data) => { if (typeof data?.seconds === "number") reelBunnyCurrentTimeRef.current = data.seconds; });
      };
      iframe.onload = () => {
        if (window.playerjs) attach();
        else { const t = setInterval(() => { if (window.playerjs) { clearInterval(t); attach(); } }, 200); }
      };
      iframe.src = `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}?t=${Math.floor(startSeconds)}`;
    });

  const getReelCurrentTime = () => {
    const clip = reelClipsRef.current[reelIndexRef.current];
    if (!clip) return null;
    if (clip.game_video_upload_id && reelVideoRef.current) return reelVideoRef.current.currentTime;
    if (clip.game_video_url && reelYtPlayerRef.current?.getCurrentTime) return reelYtPlayerRef.current.getCurrentTime();
    if (clip.game_bunny_video_id) return reelBunnyCurrentTimeRef.current;
    return null;
  };

  const playReelClipAt = async (idx) => {
    const clip = reelClipsRef.current[idx];
    if (!clip) return;
    const preRoll = clip.pre_roll_seconds ?? REEL_PRE_ROLL;
    const start = Math.max(0, clip.timestamp - preRoll);
    if (clip.game_video_upload_id) {
      if (reelYtPlayerRef.current?.pauseVideo) reelYtPlayerRef.current.pauseVideo();
      if (reelBunnyPlayerRef.current) reelBunnyPlayerRef.current.pause();
      const src = `${API}/teams/${currentTeamId}/games/${clip.game_id}/video-upload/stream`;
      const v = reelVideoRef.current;
      if (!v) return;
      if (lastReelVideoSrcRef.current !== src) {
        lastReelVideoSrcRef.current = src;
        v.src = src;
        v.load();
        const onLoaded = () => {
          v.currentTime = start;
          v.play().catch(() => {});
          v.removeEventListener("loadedmetadata", onLoaded);
        };
        v.addEventListener("loadedmetadata", onLoaded);
      } else {
        v.currentTime = start;
        v.play().catch(() => {});
      }
    } else if (clip.game_bunny_video_id) {
      if (reelYtPlayerRef.current?.pauseVideo) reelYtPlayerRef.current.pauseVideo();
      if (reelVideoRef.current) reelVideoRef.current.pause();
      const player = await ensureReelBunnyPlayer(clip.game_bunny_library_id, clip.game_bunny_video_id, start);
      if (!player) return;
      player.setCurrentTime(start);
      player.play();
    } else if (clip.game_video_url) {
      if (reelVideoRef.current) reelVideoRef.current.pause();
      if (reelBunnyPlayerRef.current) reelBunnyPlayerRef.current.pause();
      const vid = youtubeId(clip.game_video_url);
      if (!vid) return;
      const player = await ensureReelYtPlayer(vid, start);
      if (!player) return;
      player.seekTo(start, true);
      player.playVideo();
    }
  };

  const stopReel = () => {
    if (reelIntervalRef.current) {
      clearInterval(reelIntervalRef.current);
      reelIntervalRef.current = null;
    }
    if (reelVideoRef.current) reelVideoRef.current.pause();
    if (reelYtPlayerRef.current?.pauseVideo) reelYtPlayerRef.current.pauseVideo();
    if (reelBunnyPlayerRef.current) reelBunnyPlayerRef.current.pause();
    setReelActive(false);
  };

  const startReel = () => {
    const clips = events
      .filter((e) => selectedEventIds.has(e.id) && (e.game_video_url || e.game_video_upload_id || e.game_bunny_video_id))
      .sort((a, b) => (a.game_date || "").localeCompare(b.game_date || "") || a.timestamp - b.timestamp);
    if (!clips.length) {
      toast.error("None of the selected events have a video attached.");
      return;
    }
    reelClipsRef.current = clips;
    setReelClips(clips);
    reelIndexRef.current = 0;
    setReelIndexDisplay(0);
    setReelActive(true);
    // Bumping this token (rather than calling playReelClipAt(0) directly
    // here) is the actual fix for "Play opens the video but doesn't start
    // playback": the reel's <video>/YouTube container is only rendered
    // once reelActive becomes true, so reelVideoRef/reelYtContainerRef are
    // still null in this synchronous call — calling playReelClipAt here
    // silently no-ops. The effect below re-runs after React commits the
    // new DOM (refs guaranteed attached) and performs the real seek+play.
    setReelStartToken((t) => t + 1);
    if (reelIntervalRef.current) clearInterval(reelIntervalRef.current);
    reelIntervalRef.current = setInterval(() => {
      const clipsNow = reelClipsRef.current;
      const idx = reelIndexRef.current;
      const clip = clipsNow[idx];
      if (!clip) return;
      const t = getReelCurrentTime();
      const postRoll = clip.post_roll_seconds ?? REEL_POST_ROLL;
      if (t == null || t < clip.timestamp + postRoll) return;
      const nextIdx = idx + 1;
      if (nextIdx >= clipsNow.length) {
        stopReel();
        return;
      }
      reelIndexRef.current = nextIdx;
      setReelIndexDisplay(nextIdx);
      playReelClipAt(nextIdx);
    }, 300);
  };

  // Fires the actual first play once the reel's video/YouTube container is
  // guaranteed to be mounted (see the comment in startReel above).
  useEffect(() => {
    if (reelStartToken > 0) {
      playReelClipAt(reelIndexRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelStartToken]);

  const handleDownloadReel = async () => {
    const ids = Array.from(selectedEventIds);
    if (!ids.length) return;
    setReelDownloading(true);
    setReelDownloadStage("Preparing your download…");
    try {
      await downloadPlayerReel(currentTeamId, ids, (job) => setReelDownloadStage(jobStatusLabel(job)));
      toast.success("Highlight reel downloaded");
    } catch (err) {
      toast.error(err.message || "Could not build the highlight reel");
    } finally {
      setReelDownloading(false);
      setReelDownloadStage(null);
    }
  };

  const togglePlayerExpand = (id) => {
    setExpandedPlayerId((prev) => (prev === id ? null : id));
    setSelectedEventIds(new Set());
    stopReel();
  };

  const EventRow = ({ ev, showPlayer, selectable }) => (
    <div className="flex items-center gap-3 flex-wrap bg-[#0A0A0A] border border-zinc-800 rounded-sm px-4 py-2.5" data-testid={`history-event-${ev.id}`}>
      {selectable && (
        <input
          type="checkbox"
          checked={selectedEventIds.has(ev.id)}
          onChange={() => toggleEventSelected(ev.id)}
          data-testid={`history-select-${ev.id}`}
          className="w-4 h-4 accent-[#CCFF00] shrink-0 cursor-pointer"
        />
      )}
      <span className="font-label text-[10px] text-zinc-500 w-20 shrink-0">{formatGameDate(ev.game_date)}</span>
      <span className={"font-label text-[9px] uppercase px-1.5 py-0.5 rounded-sm shrink-0 " + TYPE_CLS[ev.type]}>{TYPE_LABEL[ev.type]}</span>
      <span className="font-label text-[10px] text-zinc-600 shrink-0">{formatEventTime(ev.timestamp)}</span>
      <span className="text-xs text-zinc-300 flex-1 min-w-[140px]">
        vs {ev.game_opponent}
        {showPlayer && (ev.recipient_type === "coach" ? <span className="text-zinc-500"> — Coach</span> : (ev.player_name && <span className="text-zinc-500"> — {ev.player_name}</span>))}
        {ev.assist_player_name && <span className="text-zinc-500"> · assist: {ev.assist_player_name}</span>}
        {ev.outcome && <span className="text-zinc-500 capitalize"> · {ev.outcome.replace("_", " ")}</span>}
      </span>
      <button type="button" onClick={() => navigate(`/games/${ev.game_id}`)} data-testid={`history-view-game-${ev.id}`} className="text-[#CCFF00] hover:underline text-xs font-label shrink-0">
        View game
      </button>
      <button
        type="button"
        onClick={() => handleDownload(ev)}
        disabled={downloadingId === ev.id}
        data-testid={`history-download-${ev.id}`}
        className="flex items-center gap-1 text-zinc-400 hover:text-white text-xs font-label shrink-0 disabled:opacity-50"
      >
        {downloadingId === ev.id ? <SpinnerGap size={12} className="animate-spin" /> : <DownloadSimple size={12} weight="bold" />} {downloadingId === ev.id ? clipDownloadStage : "Clip"}
      </button>
    </div>
  );

  const CleanSheetRow = ({ g }) => (
    <div className="flex items-center gap-3 flex-wrap bg-[#0A0A0A] border border-zinc-800 rounded-sm px-4 py-2.5" data-testid={`history-cleansheet-${g.game_id}`}>
      <span className="font-label text-[10px] text-zinc-500 w-20 shrink-0">{formatGameDate(g.date)}</span>
      <span className="font-label text-[9px] uppercase px-1.5 py-0.5 rounded-sm shrink-0 bg-[#32D74B]/15 text-[#32D74B]">Clean sheet</span>
      <span className="text-xs text-zinc-300 flex-1 min-w-[140px]">vs {g.opponent}{g.goalie_name && <span className="text-zinc-500"> — {g.goalie_name}</span>}</span>
      <button type="button" onClick={() => navigate(`/games/${g.game_id}`)} data-testid={`history-view-game-cs-${g.game_id}`} className="text-[#CCFF00] hover:underline text-xs font-label shrink-0">
        View game
      </button>
    </div>
  );

  const playerHistory = (playerId) => ({
    goals: events.filter((e) => e.type === "goal" && e.team === "our_team" && e.player_id === playerId),
    assists: events.filter((e) => e.type === "goal" && e.assist_player_id === playerId),
    shots: events.filter((e) => e.type === "shot" && e.team === "our_team" && e.player_id === playerId),
    saves: events.filter((e) => e.type === "save" && e.player_id === playerId),
    yellowCards: events.filter((e) => e.type === "yellow_card" && e.team === "our_team" && e.player_id === playerId),
    redCards: events.filter((e) => e.type === "red_card" && e.team === "our_team" && e.player_id === playerId),
    highlights: events.filter((e) => e.type === "highlight" && e.player_id === playerId),
    cleanSheets: cleanSheetGames.filter((g) => g.goalie_player_id === playerId),
  });

  const TOTALS_CARDS = [
    { key: "played", label: "Played", value: totals.games_played, icon: CalendarBlank, iconWeight: "bold", clickable: false },
    { key: "goals_for", label: "Goals for", value: totals.goals_for, icon: SoccerBall, iconWeight: "fill", clickable: totals.goals_for > 0 },
    { key: "goals_against", label: "Goals against", value: totals.goals_against, icon: SoccerBall, iconWeight: "regular", clickable: totals.goals_against > 0, iconColorClass: "text-zinc-500" },
    { key: "goal_diff", label: "Goal diff", value: `${totals.goal_diff > 0 ? "+" : ""}${totals.goal_diff}`, icon: ArrowsInLineVertical, iconWeight: "bold", clickable: false },
    { key: "clean_sheets", label: "Clean sheets", value: totals.clean_sheets, icon: ShieldCheck, iconWeight: "fill", clickable: totals.clean_sheets > 0 },
    { key: "yellow_cards", label: "Yellow cards", value: totals.yellow_cards, icon: Square, iconWeight: "fill", clickable: totals.yellow_cards > 0, iconColorClass: "text-yellow-400" },
    { key: "red_cards", label: "Red cards", value: totals.red_cards, icon: Square, iconWeight: "fill", clickable: totals.red_cards > 0, iconColorClass: "text-red-500" },
  ];

  const drilldownEvents = () => {
    if (teamDrilldown === "goals_for") return events.filter((e) => e.type === "goal" && e.team === "our_team");
    if (teamDrilldown === "goals_against") return events.filter((e) => e.type === "goal" && e.team === "opposing_team");
    if (teamDrilldown === "yellow_cards") return events.filter((e) => e.type === "yellow_card" && e.team === "our_team");
    if (teamDrilldown === "red_cards") return events.filter((e) => e.type === "red_card" && e.team === "our_team");
    return [];
  };

  const SortHeader = ({ label, sortKey, align = "left" }) => {
    const active = sort.key === sortKey;
    return (
      <button
        type="button"
        onClick={() => handleSort(sortKey)}
        data-testid={`stats-sort-${sortKey}`}
        className={`font-label text-[10px] uppercase flex items-center gap-0.5 ${align === "right" ? "justify-end w-full" : ""} ${active ? "text-[#CCFF00]" : "text-zinc-500 hover:text-zinc-300"} transition-colors duration-150`}
      >
        {label}
        {active && (sort.dir === "asc" ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)}
      </button>
    );
  };

  return (
    <Layout>
      <div className="mb-8">
        <p className="font-label text-xs text-[#CCFF00] mb-1">{currentTeam?.name}</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none">Season Stats</h1>
        <p className="text-zinc-500 text-sm mt-2">Built entirely from logged Events — every number here is clickable through to its clip.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 mb-4">
            {TOTALS_CARDS.map((c) => {
              const Icon = c.icon;
              const isOpen = teamDrilldown === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  disabled={!c.clickable}
                  onClick={() => setTeamDrilldown(isOpen ? null : c.key)}
                  data-testid={`total-${c.key.replace(/_/g, "-")}`}
                  className={`text-left bg-[#121212] border p-6 rounded-sm transition-colors duration-150 ${c.clickable ? "border-zinc-800 hover:border-[#CCFF00]/50 cursor-pointer" : "border-zinc-800 cursor-default"} ${isOpen ? "border-[#CCFF00]" : ""}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-label text-xs text-zinc-500">{c.label}</span>
                    <Icon size={20} className={c.iconColorClass || "text-[#CCFF00]"} weight={c.iconWeight} />
                  </div>
                  <p className={`font-heading text-5xl leading-none ${c.key === "goal_diff" ? (totals.goal_diff > 0 ? "text-[#32D74B]" : totals.goal_diff < 0 ? "text-[#FF3B30]" : "text-white") : "text-white"}`}>{c.value}</p>
                </button>
              );
            })}
          </div>

          <AnimatePresence>
            {teamDrilldown && (
              <motion.div
                {...EXPAND_COLLAPSE_ANIMATION}
                className="overflow-hidden mb-8"
                data-testid="team-drilldown"
              >
                <div className="bg-[#0F0F0F] border border-zinc-800 rounded-sm p-4 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-label text-xs text-zinc-400 uppercase">
                      {DRILLDOWN_TITLES[teamDrilldown]}
                    </p>
                    <button type="button" onClick={() => setTeamDrilldown(null)} data-testid="team-drilldown-close" className="text-zinc-500 hover:text-white transition-colors duration-150">
                      <X size={16} />
                    </button>
                  </div>
                  {teamDrilldown === "clean_sheets"
                    ? (cleanSheetGames.length === 0
                        ? <p className="text-zinc-600 text-sm">No clean sheets yet.</p>
                        : cleanSheetGames.map((g) => <CleanSheetRow key={g.game_id} g={g} />))
                    : (drilldownEvents().length === 0
                        ? <p className="text-zinc-600 text-sm">Nothing logged yet.</p>
                        : drilldownEvents().map((ev) => <EventRow key={ev.id} ev={ev} showPlayer />))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {players.length === 0 ? (
            <div className="bg-[#121212] border border-dashed border-zinc-800 rounded-sm py-20 flex flex-col items-center" data-testid="stats-empty">
              <ChartBar size={40} className="text-zinc-700 mb-4" />
              <p className="text-zinc-400 mb-1 font-heading text-2xl">No stats yet</p>
              <p className="text-zinc-600 text-sm">Add players, then log Goal/Shot/Save events on your games.</p>
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-sm overflow-x-auto" data-testid="stats-table">
              <div className={TABLE_MIN_WIDTH}>
              <div className={`grid ${GRID_COLS} gap-2 px-4 py-3 bg-[#121212] border-b border-zinc-800`}>
                <SortHeader label="#" sortKey="jersey_number" />
                <SortHeader label="Player" sortKey="name" />
                <SortHeader label="Pos" sortKey="position" />
                <SortHeader label="G" sortKey="goals" align="right" />
                <SortHeader label="A" sortKey="assists" align="right" />
                <SortHeader label="Sh" sortKey="shots" align="right" />
                <SortHeader label="Sv" sortKey="saves" align="right" />
                <SortHeader label="YC" sortKey="yellow_cards" align="right" />
                <SortHeader label="RC" sortKey="red_cards" align="right" />
                <SortHeader label="CS" sortKey="clean_sheets" align="right" />
                <span />
              </div>
              {sortedPlayers.map((p, i) => {
                const isOpen = expandedPlayerId === p.id;
                const hist = isOpen ? playerHistory(p.id) : null;
                return (
                  <div key={p.id}>
                    <motion.button
                      type="button"
                      initial={STAGGER_ROW_INITIAL}
                      animate={STAGGER_ROW_ANIMATE}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => togglePlayerExpand(p.id)}
                      data-testid={`stats-row-${p.jersey_number}`}
                      className={`w-full text-left grid ${GRID_COLS} gap-2 px-4 py-3 items-center border-b border-zinc-900 hover:bg-white/5 transition-colors duration-150`}
                    >
                      <span className="font-heading text-xl text-[#CCFF00]">{p.jersey_number}</span>
                      <div className="min-w-0 flex items-center gap-2">
                        <p className="text-white font-semibold text-sm truncate" data-testid={`stats-name-${p.jersey_number}`}>
                          {p.first_name} {p.last_name}
                        </p>
                        {!p.active && <span className="font-label text-[9px] text-zinc-600 bg-zinc-800/50 px-1.5 py-0.5 rounded-sm shrink-0">INACTIVE</span>}
                      </div>
                      <span className="text-zinc-500 text-xs truncate" data-testid={`stats-position-${p.jersey_number}`}>{p.position || "—"}</span>
                      <span className={`text-right font-heading text-lg ${p.goals > 0 ? "text-white" : "text-zinc-700"}`} data-testid={`stats-goals-${p.jersey_number}`}>{p.goals}</span>
                      <span className={`text-right font-heading text-lg ${p.assists > 0 ? "text-white" : "text-zinc-700"}`} data-testid={`stats-assists-${p.jersey_number}`}>{p.assists}</span>
                      <span className={`text-right font-heading text-lg ${p.shots > 0 ? "text-white" : "text-zinc-700"}`} data-testid={`stats-shots-${p.jersey_number}`}>{p.shots}</span>
                      <span className={`text-right font-heading text-lg ${p.saves > 0 ? "text-white" : "text-zinc-700"}`} data-testid={`stats-saves-${p.jersey_number}`}>{p.saves}</span>
                      <span className={`text-right font-heading text-lg ${p.yellow_cards > 0 ? "text-yellow-400" : "text-zinc-700"}`} data-testid={`stats-yellow-${p.jersey_number}`}>{p.yellow_cards}</span>
                      <span className={`text-right font-heading text-lg ${p.red_cards > 0 ? "text-red-500" : "text-zinc-700"}`} data-testid={`stats-red-${p.jersey_number}`}>{p.red_cards}</span>
                      <span className={`text-right font-heading text-lg ${p.clean_sheets > 0 ? "text-white" : "text-zinc-700"}`} data-testid={`stats-clean-${p.jersey_number}`}>{p.clean_sheets}</span>
                      {isOpen ? <CaretUp size={14} className="text-zinc-500 justify-self-end" /> : <CaretDown size={14} className="text-zinc-500 justify-self-end" />}
                    </motion.button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          {...EXPAND_COLLAPSE_ANIMATION}
                          className="overflow-hidden bg-[#0A0A0A] border-b border-zinc-900"
                          data-testid={`player-history-${p.jersey_number}`}
                        >
                          <div className="p-4 space-y-3">
                            {selectedEventIds.size > 0 && (
                              <div className="flex items-center justify-between gap-3 flex-wrap bg-[#12130a] border border-[#CCFF00]/30 rounded-sm px-3 py-2" data-testid="player-reel-toolbar">
                                <span className="font-label text-xs text-[#CCFF00]">{selectedEventIds.size} clip{selectedEventIds.size > 1 ? "s" : ""} selected</span>
                                <div className="flex items-center gap-3">
                                  <button type="button" onClick={startReel} data-testid="player-reel-play-btn" className="flex items-center gap-1 font-label text-xs text-[#CCFF00] hover:text-[#B3E600] transition-colors duration-150">
                                    <Play size={13} weight="fill" /> {reelActive ? "Restart" : "Play"}
                                  </button>
                                  {!isViewer && (
                                    <button type="button" onClick={handleDownloadReel} disabled={reelDownloading} data-testid="player-reel-download-btn" className="flex items-center gap-1 font-label text-xs text-zinc-300 hover:text-white transition-colors duration-150 disabled:opacity-50">
                                      {reelDownloading ? <SpinnerGap size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />} {reelDownloading ? reelDownloadStage : "Download reel"}
                                    </button>
                                  )}
                                  <button type="button" onClick={() => { setSelectedEventIds(new Set()); stopReel(); }} data-testid="player-reel-clear-btn" className="font-label text-xs text-zinc-500 hover:text-white transition-colors duration-150">
                                    Clear
                                  </button>
                                </div>
                              </div>
                            )}
                            {reelActive && (() => {
                              const clip = reelClips[reelIndexDisplay];
                              return (
                                <div className="relative w-full overflow-hidden rounded-sm bg-black" style={{ paddingTop: "56.25%" }} data-testid="player-reel-video">
                                  <div ref={reelYtContainerRef} className={`absolute inset-0 w-full h-full ${clip?.game_video_upload_id || clip?.game_bunny_video_id ? "hidden" : ""}`} />
                                  <video ref={reelVideoRef} controls className={`absolute inset-0 w-full h-full ${clip?.game_video_upload_id ? "" : "hidden"}`} />
                                  <iframe
                                    ref={reelBunnyIframeRef}
                                    data-testid="player-reel-bunny-iframe"
                                    className={`absolute inset-0 w-full h-full border-0 ${clip?.game_bunny_video_id ? "" : "hidden"}`}
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                  />
                                  {clip && (
                                    <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2 bg-black/70 backdrop-blur-sm rounded-sm px-3 py-2">
                                      <span className="font-label text-xs text-white">
                                        Clip {reelIndexDisplay + 1} of {reelClips.length} — {TYPE_LABEL[clip.type]} vs {clip.game_opponent}
                                      </span>
                                      <button type="button" onClick={stopReel} data-testid="player-reel-stop-btn" className="flex items-center gap-1 font-label uppercase text-[10px] text-white px-2 py-1 rounded-sm bg-white/10 hover:bg-white/20 transition-colors duration-150">
                                        <Stop size={11} weight="fill" /> Stop
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {[
                              { label: "Goals", list: hist.goals, showPlayer: false },
                              { label: "Assists", list: hist.assists, showPlayer: true },
                              { label: "Shots", list: hist.shots, showPlayer: false },
                              { label: "Saves", list: hist.saves, showPlayer: false },
                              { label: "Yellow cards", list: hist.yellowCards, showPlayer: false },
                              { label: "Red cards", list: hist.redCards, showPlayer: false },
                              { label: "Highlights", list: hist.highlights, showPlayer: false },
                            ].map((group) => group.list.length > 0 && (
                              <div key={group.label}>
                                <p className="font-label text-[10px] text-zinc-500 uppercase mb-1.5">{group.label} ({group.list.length})</p>
                                <div className="space-y-1.5">
                                  {group.list.map((ev) => <EventRow key={ev.id} ev={ev} showPlayer={group.showPlayer} selectable />)}
                                </div>
                              </div>
                            ))}
                            {hist.cleanSheets.length > 0 && (
                              <div>
                                <p className="font-label text-[10px] text-zinc-500 uppercase mb-1.5">Clean sheets ({hist.cleanSheets.length})</p>
                                <div className="space-y-1.5">
                                  {hist.cleanSheets.map((g) => <CleanSheetRow key={g.game_id} g={g} />)}
                                </div>
                              </div>
                            )}
                            {hist.goals.length === 0 && hist.assists.length === 0 && hist.shots.length === 0 && hist.saves.length === 0 && hist.yellowCards.length === 0 && hist.redCards.length === 0 && hist.highlights.length === 0 && hist.cleanSheets.length === 0 && (
                              <p className="text-zinc-600 text-xs" data-testid={`player-history-empty-${p.jersey_number}`}>No events logged for this player yet.</p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {players.length > 0 && withStats.length === 0 && (
            <p className="text-zinc-600 text-sm mt-4">No events logged yet. Open a game and tap "Add event" to build the leaderboard.</p>
          )}
        </>
      )}
    </Layout>
  );
}
